import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { reportLinks } from '../lib/api'
import { Icon } from './ui'

type Command = {
  id: string
  label: string
  hint?: string
  keywords: string
  icon: string
  run: () => void
}

const NAV: { label: string; to: string; icon: string; keywords: string }[] = [
  { label: 'ภาพรวม', to: '/', icon: 'dashboard', keywords: 'dashboard home overview ภาพรวม' },
  { label: 'ข้อสังเกต', to: '/findings', icon: 'findings', keywords: 'findings ข้อสังเกต ปัญหา' },
  { label: 'เคสตรวจสอบ', to: '/cases', icon: 'cases', keywords: 'cases เคส สอบสวน' },
  { label: 'โครงการตรวจสอบ', to: '/engagements', icon: 'engagements', keywords: 'engagements โครงการ audit' },
  { label: 'เอกสาร', to: '/documents', icon: 'documents', keywords: 'documents เอกสาร ไฟล์' },
  { label: 'การเรียกเก็บเงิน', to: '/billing', icon: 'billing', keywords: 'billing invoice ใบแจ้งหนี้ เก็บเงิน' },
  { label: 'ความปลอดภัย', to: '/security', icon: 'lock', keywords: 'security mfa pdpa ความปลอดภัย' },
  { label: 'ร่องรอยการตรวจสอบ', to: '/audit-trail', icon: 'shield', keywords: 'audit trail ร่องรอย log' },
]

export function CommandPalette({
  open,
  onClose,
  onSignOut,
}: {
  open: boolean
  onClose: () => void
  onSignOut: () => void
}) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const commands = useMemo<Command[]>(() => {
    const nav: Command[] = NAV.map((n) => ({
      id: `nav:${n.to}`,
      label: n.label,
      hint: n.to,
      keywords: n.keywords,
      icon: n.icon,
      run: () => navigate(n.to),
    }))
    const actions: Command[] = [
      {
        id: 'action:pdf',
        label: 'ดาวน์โหลดรายงาน PDF',
        hint: 'รายงานข้อสังเกต',
        keywords: 'pdf report รายงาน ดาวน์โหลด',
        icon: 'documents',
        run: () => {
          void reportLinks().then((l) => window.open(l.pdf_url, '_blank', 'noopener'))
        },
      },
      {
        id: 'action:signout',
        label: 'ออกจากระบบ',
        keywords: 'logout signout ออกจากระบบ',
        icon: 'lock',
        run: onSignOut,
      },
    ]
    return [...nav, ...actions]
  }, [navigate, onSignOut])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    return commands.filter((c) => `${c.label} ${c.keywords}`.toLowerCase().includes(q))
  }, [commands, query])

  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
      // Autofocus after the dialog paints.
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    setActive(0)
  }, [query])

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  if (!open) return null

  const choose = (cmd: Command | undefined) => {
    if (!cmd) return
    onClose()
    cmd.run()
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50, background: 'var(--color-scrim)',
        backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'start center', paddingTop: '12vh',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="ค้นหาและคำสั่ง"
        onClick={(e) => e.stopPropagation()}
        className="card rise"
        style={{ width: 'min(560px, 92vw)', padding: 8, boxShadow: 'var(--shadow-sheet)', background: 'var(--color-surface-1)' }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => Math.min(i + 1, filtered.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            choose(filtered[active])
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
          }
        }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px' }}>
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            className="input"
            placeholder="พิมพ์เพื่อค้นหา…"
            aria-label="ค้นหาคำสั่ง"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ border: 'none', background: 'transparent', flex: 1 }}
          />
          <span className="kbd">ESC</span>
        </div>
        <div className="rule" />
        <div ref={listRef} role="listbox" aria-label="คำสั่ง" style={{ maxHeight: 360, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div className="dim" style={{ padding: '18px 14px', fontSize: 12.5 }}>ไม่พบคำสั่งที่ตรงกับ “{query}”</div>
          ) : (
            filtered.map((c, i) => (
              <button
                key={c.id}
                data-index={i}
                role="option"
                aria-selected={i === active}
                className="nav-item"
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(c)}
                style={{
                  width: '100%', textAlign: 'left', marginTop: 2,
                  background: i === active ? 'var(--color-surface-2)' : 'transparent',
                }}
              >
                <Icon name={c.icon} size={15} />
                <span style={{ flex: 1 }}>{c.label}</span>
                {c.hint ? <span style={{ fontSize: 11, color: "var(--color-ivory-muted)" }}>{c.hint}</span> : null}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
